"use client";

import { TheaterRailIconButton } from "@/components/feature/theater-rail-icon-button";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { GameControllerIcon, PlayCircleIcon } from "@phosphor-icons/react";

/** Original Digit pill: used on portrait-mobile bottom chrome. */
const VIDEO_PILL_BUTTON_CLASS =
  "relative z-10 rounded-full bg-brand-purple py-6 pl-6 pr-4 text-md font-bold text-white hover:bg-brand-purple-dark";

function VideoDecorationHead() {
  return (
    <img
      src="/images/games/decoration-videoButton-head.webp"
      alt=""
      className="pointer-events-none absolute -right-1.5 -top-2 z-0 h-[43px] w-[47px] -translate-y-1/2 transform transition-transform duration-200 group-hover:-translate-x-[-1%] group-hover:-translate-y-[55%]"
    />
  );
}

function VideoDecorationHands() {
  return (
    <img
      src="/images/games/decoration-videoButton-hands.webp"
      alt=""
      className="pointer-events-none absolute -right-1 -top-1 z-40 h-[21.5px] w-[43px] transition-transform duration-200 group-hover:translate-x-[-1%] group-hover:translate-y-[4%]"
    />
  );
}

function VideoToggleIcons({ isShowingVideo }: { isShowingVideo: boolean }) {
  return (
    <span className="inline-grid [&>*]:col-start-1 [&>*]:row-start-1">
      <PlayCircleIcon
        weight="fill"
        size={24}
        strokeWidth={3}
        className={isShowingVideo ? "invisible" : undefined}
        aria-hidden
      />
      <GameControllerIcon
        weight="fill"
        size={24}
        strokeWidth={3}
        className={!isShowingVideo ? "invisible" : undefined}
        aria-hidden
      />
    </span>
  );
}

function VideoTogglePill({
  isShowingVideo,
  onVideoToggle,
  className,
}: {
  isShowingVideo: boolean;
  onVideoToggle?: () => void;
  className?: string;
}) {
  const label = isShowingVideo ? "Play" : "Video";

  return (
    <div className={cn("relative group", className)}>
      {!isShowingVideo && <VideoDecorationHead />}
      <Button
        onClick={onVideoToggle}
        className={VIDEO_PILL_BUTTON_CLASS}
        aria-label={label}
        title={label}
        data-testid="video-toggle-pill"
      >
        <span className="inline-grid [&>*]:col-start-1 [&>*]:row-start-1">
          <span className={isShowingVideo ? "invisible" : undefined}>Video</span>
          <span className={!isShowingVideo ? "invisible" : undefined}>Play</span>
        </span>
        <VideoToggleIcons isShowingVideo={isShowingVideo} />
      </Button>
      {!isShowingVideo && <VideoDecorationHands />}
    </div>
  );
}

interface VideoToggleButtonProps {
  isShowingVideo: boolean;
  onVideoToggle?: () => void;
}

/**
 * Side rail uses the compact circle; portrait mobile keeps the original Digit pill.
 * CSS shows one at a time so Digit and pill size stay pixel-true on phones.
 */
export function VideoToggleButton({ isShowingVideo, onVideoToggle }: VideoToggleButtonProps) {
  const label = isShowingVideo ? "Play" : "Video";

  return (
    <>
      <div
        className="relative z-40 overflow-visible group max-md:portrait:!hidden"
        data-testid="video-toggle-rail"
      >
        {!isShowingVideo && <VideoDecorationHead />}
        <TheaterRailIconButton
          label={label}
          icon={<VideoToggleIcons isShowingVideo={isShowingVideo} />}
          onClick={onVideoToggle}
        />
        {!isShowingVideo && <VideoDecorationHands />}
      </div>
      <VideoTogglePill
        isShowingVideo={isShowingVideo}
        onVideoToggle={onVideoToggle}
        className="hidden max-md:portrait:!block"
      />
    </>
  );
}
